/**
 * A USD wallet that paid a CAD session (Warren, 2026-10-25T02:52:41Z, option
 * 1: "Convert at the moment they pay ... shows the rate on the receipt").
 * The session carries the debit (wallet_fx_debit): the USD taken, the CAD it
 * paid and the Bank of Canada rate pinned at start. The receipt, the session
 * detail and the history rows read it through here, so all three print the
 * same amounts and the same rate ("at 1.3993 USD to CAD, Bank of Canada,
 * 2026-10-23").
 *
 * Pure (no React), so node tests run it directly.
 */
import { formatWalletFxRate } from '@evi/shared';
import type { SessionWalletFxDebit } from './api';

export interface SessionWalletFxLine {
  /** USD taken from the wallet. */
  usdCents: number;
  /** CAD of the session it paid. */
  cadCents: number;
  /** The rate as printed: four decimals. */
  rate: string;
  /** The Bank of Canada business day of the rate, YYYY-MM-DD. */
  date: string;
}

const cents = (n: unknown): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;

/** The line, or null for a session no converted wallet paid. */
export function sessionWalletFxLine(s: { wallet_fx_debit?: SessionWalletFxDebit | null } | null | undefined): SessionWalletFxLine | null {
  const d = s?.wallet_fx_debit;
  if (!d || d.pair !== 'USDCAD' || d.wallet_currency !== 'USD' || d.session_currency !== 'CAD'
    || !cents(d.debit_cents) || !cents(d.paid_cents) || d.paid_cents === 0
    || typeof d.rate !== 'number' || !Number.isFinite(d.rate) || typeof d.rate_date !== 'string') return null;
  return { usdCents: d.debit_cents, cadCents: d.paid_cents, rate: formatWalletFxRate(d.rate), date: d.rate_date };
}

/** The receipt PDF's English lines (the PDF is English only), two so they
 *  fit the page width beside nothing else. */
export function walletFxReceiptLines(line: SessionWalletFxLine, money: (cents: number, currency: string) => string): [string, string] {
  return [`Paid ${money(line.usdCents, 'USD')} from your USD wallet for ${money(line.cadCents, 'CAD')},`,
    `at ${line.rate} USD to CAD, Bank of Canada, ${line.date}.`];
}
